<template>
    <div ref="rootEl" class="main-div" :class="width">
        <div class="header-div">
            <div class="title-div">
                <div class="icon-text-header">
                    <div class="icon-div">
                        <svg xmlns="http://www.w3.org/2000/svg" width="380" height="380" viewBox="0 0 480 480" fill="none" class="pattern" opacity="0.4">
                            <defs>
                                <clipPath id="clip-circle">
                                    <rect x="190" y="190" width="240" height="240" />
                                </clipPath>
                            </defs>
                            <g clip-path="url(#clip-circle)">
                                <circle cx="240" cy="240" r="60" stroke="currentColor" />
                                <circle cx="240" cy="240" r="92" stroke="currentColor" />
                                <circle cx="240" cy="240" r="124" stroke="currentColor" />
                                <circle cx="240" cy="240" r="156" stroke="currentColor" />
                            </g>
                        </svg>
                        <slot name="icon" />
                    </div>
                    <div>
                        <div class="title-section">
                            <h2 :id="titleId" ref="titleEl" tabindex="-1">{{ title }}</h2>
                            <!-- Where a modal renders its own close control: the dialog is opened
                                 with `closable: false`, because PrimeVue's own close button comes
                                 with a header bar above the card. -->
                            <slot name="header-action" />
                        </div>
                        <p class="sub-title">{{ subtitle }}</p>
                    </div>
                </div>
                <div class="title-wrapper" :class="bodyClass">
                    <div class="divider" :class="dividerClass"></div>
                    <slot />
                </div>
            </div>
        </div>
        <div v-if="$slots.footer" class="modal-footer" :class="footerClass">
            <div class="footer-actions">
                <slot name="footer" />
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { nextTick, onMounted, ref, useId } from 'vue';

withDefaults(
    defineProps<{
        title: string;
        subtitle: string;
        width?: string;
        bodyClass?: string;
        dividerClass?: string;
        footerClass?: string;
    }>(),
    {
        width: 'sm:max-w-xl sm:w-full w-11/12',
        bodyClass: 'gap-1',
        dividerClass: 'mt-4 mb-3.5',
        footerClass: 'pt-8',
    }
);

const rootEl = ref<HTMLElement | null>(null);
const titleEl = ref<HTMLElement | null>(null);
const titleId = useId();

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// PrimeVue only enters a dialog when it finds an [autofocus] descendant, and `closable: false` leaves
// no close button to fall back on, so without this the modal opens with focus still on its trigger.
onMounted(async () => {
    await nextTick();

    // Name the dialog by REFERENCE to the heading it is already showing. `useDynamicDialog` can
    // only set `aria-label` from what the title was at the press, so a title that changes while
    // the dialog is open — the member narrowing a confirmation's list — leaves a screen reader
    // announcing a count that is no longer on screen. Pointing at the live heading means the
    // accessible name is the visible one, always. The opener's `aria-label` would win over
    // `aria-labelledby`, so it goes.
    const dialog = rootEl.value?.closest('[role="dialog"]');
    if (dialog) {
        dialog.setAttribute('aria-labelledby', titleId);
        dialog.removeAttribute('aria-label');
    }

    const focusable = Array.from(rootEl.value?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const target = focusable.find((el) => el.offsetParent !== null) ?? titleEl.value;

    target?.focus();
});
</script>
<style lang="scss" scoped>
.main-div {
    @apply rounded-lg bg-white mx-auto h-auto overflow-y-auto;
}
.header-div {
    @apply flex flex-row items-start gap-4 self-stretch;
}
.title-div {
    @apply flex flex-col p-6 pb-0 items-start gap-1 self-stretch w-full;
}
.icon-text-header {
    @apply flex flex-row items-start gap-4 w-full;
}
// The single host for every glyph slotted into `#icon`: `color` inherits into slot
// content, so a slotted svg drawn in `currentColor` needs no colour of its own.
// Near-black in light, so it is the primary ink tier.
.icon-div {
    @apply relative flex w-12 h-12 p-3 justify-center items-center rounded-full bg-gray-100 flex-shrink-0;
    color: var(--ink);
}
.pattern {
    @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none;
    color: var(--hairline);
}
.title-section {
    @apply self-stretch flex flex-row justify-between relative;

    h2 {
        @apply text-zinc-950 text-lg font-semibold leading-7;
    }
}
.sub-title {
    @apply text-zinc-600 text-sm font-normal leading-5;
}
.title-wrapper {
    @apply flex flex-col items-start w-full sm:pl-16;
}
.divider {
    @apply h-px bg-zinc-300 w-full;
}
.modal-footer {
    @apply flex flex-col justify-center items-end self-stretch;

    .footer-actions {
        @apply flex justify-end items-center gap-3 flex-1 px-6 pb-6;
    }
}
</style>
